import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Users, Wallet, TrendingUp, Loader2, CheckCircle, Clock, FileText, Store
} from 'lucide-react';
import { ToppedUpCreditsReconcileModal } from './ToppedUpCreditsReconcileModal';

export function AdminDashboard() {
  const { api } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [stockOverview, setStockOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconcileOpen, setReconcileOpen] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [d, s] = await Promise.all([
          api.get('/api/admin/dashboard'),
          api.get('/api/admin/stocks/overview').catch(() => ({ data: null })),
        ]);
        setDashboard(d.data);
        setStockOverview(s.data);
      } catch (error) {
        console.error('Failed to fetch dashboard:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [api]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', minimumFractionDigits: 0 }).format(amount || 0);

  const formatUSD = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount || 0);

  const formatShares = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

  if (loading) {
    return (
      <AdminSidebarLayout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
        </div>
      </AdminSidebarLayout>
    );
  }

  const merchants = dashboard?.merchants || {};
  const portfolio = dashboard?.portfolio || {};
  const stock = stockOverview || {};

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-dashboard">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="font-outfit text-2xl font-bold text-white">Dashboard</h1>
            <p className="text-[#5C7599] text-sm mt-0.5">Platform overview and key metrics</p>
          </div>
          <div className="flex items-center gap-2 text-[#5C7599] text-xs">
            <Clock className="w-3.5 h-3.5" />
            {new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
        </div>

        {/* Primary KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            {
              label: 'Members',
              value: dashboard?.members?.total,
              extra: dashboard?.new_members_7d > 0 ? `+${dashboard.new_members_7d} this week` : null,
              breakdown: (
                <p className="text-[9px] text-[#5C7599] mt-0.5">
                  <span className="text-[#00E676]">{dashboard?.members?.paid || 0} paid</span>
                  {' · '}
                  <span className="text-[#00E5FF]">{dashboard?.members?.free || 0} free</span>
                  <span className="text-[#5C7599]"> ({dashboard?.members?.free_batch || 0} batch · {dashboard?.members?.free_manual || 0} manual)</span>
                </p>
              ),
              icon: Users,
              color: '#00E5FF',
              testid: 'admin-members-card',
            },
            { label: 'Active Subs', value: dashboard?.active_subscriptions, extra: dashboard?.new_subs_7d > 0 ? `+${dashboard.new_subs_7d} this week` : null, icon: CheckCircle, color: '#00E676' },
            { label: 'Membership Pool', value: formatCurrency(dashboard?.total_initial_value), icon: Wallet, color: '#007AFF', raw: true },
            { label: 'Pending Withdrawals', value: dashboard?.pending_withdrawals, icon: Clock, color: '#FFEA00' },
          ].map(kpi => (
            <Card key={kpi.label} className="bg-[#041A3B] border-white/10" data-testid={kpi.testid || `admin-${kpi.label.toLowerCase().replace(/\s/g, '-')}-card`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${kpi.color}15` }}>
                    <kpi.icon className="w-5 h-5" style={{ color: kpi.color }} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[#5C7599] text-[10px] uppercase tracking-wider">{kpi.label}</p>
                    <p className="font-outfit text-xl font-bold text-white">{kpi.raw ? kpi.value : (kpi.value || 0)}</p>
                    {kpi.extra && <p className="text-[#00E676] text-[10px]">{kpi.extra}</p>}
                    {kpi.breakdown}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stock Certificate & Merchant Stats */}
        <div className="grid lg:grid-cols-2 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-[#041A3B] to-[#0A254D] border-[#00E5FF]/20" data-testid="admin-stock-overview">
            <CardContent className="p-5">
              <h3 className="font-outfit text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#00E5FF]" /> Stock Certificate
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Certificates Issued', value: formatShares(stock.total_certificates), sub: 'subscribed members' },
                  { label: 'Units in Circulation', value: formatShares(stock.units_in_circulation), sub: `${formatUSD(stock.units_in_circulation * (stock.current_unit_usd || 1))} value`, green: true },
                  { label: 'Unit Value', value: formatUSD(stock.current_unit_usd || 1), sub: `× ${(stock.growth_multiplier || 1).toFixed(2)} growth` },
                  { label: 'Pending Trades', value: formatShares(stock.pending_trades), sub: `${stock.completed_trades || 0} completed`, amber: !!stock.pending_trades },
                ].map(s => (
                  <div key={s.label} className="p-3 rounded-lg bg-[#000B18]/60">
                    <p className="text-[#5C7599] text-[10px] uppercase">{s.label}</p>
                    <p className={`font-mono text-sm font-bold ${s.green ? 'text-[#00E676]' : s.amber ? 'text-amber-400' : 'text-white'}`}>{s.value}</p>
                    {s.sub && <p className="text-[#5C7599] text-[9px]">{s.sub}</p>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-5">
              <h3 className="font-outfit text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Store className="w-4 h-4 text-[#00E676]" /> Merchant Partners
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Approved', value: merchants.approved || 0, color: 'text-[#00E676]' },
                  { label: 'Pending', value: merchants.pending || 0, color: 'text-amber-400' },
                  { label: 'Active Services', value: merchants.total_services || 0, color: 'text-white' },
                  { label: 'MROs', value: dashboard?.mros?.total || 0, color: 'text-[#00E5FF]' },
                ].map(s => (
                  <div key={s.label} className="p-3 rounded-lg bg-[#000B18]/60">
                    <p className="text-[#5C7599] text-[10px] uppercase">{s.label}</p>
                    <p className={`font-outfit text-xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Wallet Summary */}
        <Card className="bg-[#041A3B] border-white/10 mb-6">
          <CardContent className="p-5">
            <h3 className="font-outfit text-sm font-semibold text-white mb-3">Wallet Summary</h3>
            {/* Non-withdrawable membership pool */}
            <p className="text-[#5C7599] text-[9px] uppercase tracking-widest mb-2">Non-withdrawable membership pool</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              {[
                { label: 'Initial Pool', value: formatCurrency(dashboard?.total_initial_value), sub: 'Sum of what members paid at activation', color: 'text-white', testid: 'wallet-initial-pool' },
                { label: 'Growth', value: formatCurrency(dashboard?.total_growth_value), sub: 'Unit-value appreciation on top of initial pool', color: 'text-[#00E5FF]', testid: 'wallet-growth' },
                { label: 'Membership Value', value: formatCurrency(dashboard?.total_managed_value), sub: 'Initial Pool + Growth', color: 'text-white', testid: 'wallet-membership-value' },
              ].map(s => (
                <div key={s.label} className="p-3 rounded-lg bg-[#000B18]/60" data-testid={s.testid}>
                  <p className="text-[#5C7599] text-[10px] uppercase">{s.label}</p>
                  <p className={`font-mono text-sm font-bold ${s.color}`}>{s.value}</p>
                  {s.sub && <p className="text-[#5C7599] text-[9px] leading-tight mt-0.5">{s.sub}</p>}
                </div>
              ))}
            </div>
            {/* Spendable / withdrawable */}
            <p className="text-[#5C7599] text-[9px] uppercase tracking-widest mb-2">Spendable balance</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: 'Referral Earnings', value: formatCurrency(dashboard?.total_referral_earnings), sub: 'From referral commissions', color: 'text-[#00E676]', testid: 'wallet-referral-earnings' },
                { label: 'Topped-up Credits', value: formatCurrency(dashboard?.total_topped_up_credits), sub: 'Stock trades, refunds, adjustments', color: 'text-[#FFEA00]', testid: 'wallet-topped-up', reconcilable: true },
                { label: 'Pending Payouts', value: dashboard?.pending_withdrawals || 0, sub: 'Awaiting admin release', color: 'text-amber-400', testid: 'wallet-pending-payouts' },
              ].map(s => (
                <div key={s.label} className="p-3 rounded-lg bg-[#000B18]/60 relative" data-testid={s.testid}>
                  <p className="text-[#5C7599] text-[10px] uppercase">{s.label}</p>
                  <p className={`font-mono text-sm font-bold ${s.color}`}>{s.value}</p>
                  {s.sub && <p className="text-[#5C7599] text-[9px] leading-tight mt-0.5">{s.sub}</p>}
                  {s.reconcilable && (
                    <button
                      type="button"
                      onClick={() => setReconcileOpen(true)}
                      className="absolute top-1.5 right-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-[#FFEA00]/15 text-[#FFEA00] hover:bg-[#FFEA00]/25 border border-[#FFEA00]/30 transition"
                      data-testid="reconcile-topped-up-btn"
                    >
                      Reconcile
                    </button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <ToppedUpCreditsReconcileModal open={reconcileOpen} onOpenChange={setReconcileOpen} />

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { href: '/admin/users', label: 'Manage Users', icon: Users, color: '#00E5FF' },
            { href: '/admin/portfolio', label: 'Portfolio', icon: TrendingUp, color: '#00E676' },
            { href: '/admin/merchants', label: 'Merchant Partners', icon: Store, color: '#FFEA00' },
            { href: '/admin/reports', label: 'Reports', icon: FileText, color: '#007AFF' },
          ].map(a => (
            <a key={a.href} href={a.href}>
              <Button className="w-full h-auto py-3 btn-secondary flex flex-col items-center gap-1.5 text-xs" data-testid={`admin-${a.label.toLowerCase().replace(/\s/g, '-')}-btn`}>
                <a.icon className="w-5 h-5" style={{ color: a.color }} />
                {a.label}
              </Button>
            </a>
          ))}
        </div>
      </div>
    </AdminSidebarLayout>
  );
}
